import React, { useState, useCallback, useEffect } from 'react';
import {
  BsWhatsapp,
  BsSend,
  BsMicrosoftTeams,
  BsFacebook,
  BsEnvelope,
  BsCameraVideo,
  BsSlack,
  BsFileSpreadsheet,
} from 'react-icons/bs';
import WhatsAppConnectModal from './WhatsAppConnectModal';
import { checkChannelStatus } from '../../services/whatsappStatusService';
import './FeatureSetTiles.css';

const WHATSAPP_PHONE_STORAGE_KEY = 'luca-whatsapp-phone';
const WHATSAPP_PHONE_SESSION_KEY = 'luca-whatsapp-phone-session';
const WHATSAPP_STATUS_SESSION_KEY = 'luca-whatsapp-status-session';

const TILES = [
  {
    id: 'whatsapp',
    label: 'WhatsApp',
    status: 'Idle',
    description: 'Customers can contact you directly from WhatsApp',
    Icon: BsWhatsapp,
    color: '#25D366',
  },
  {
    id: 'google-sheets',
    label: 'Google Sheets',
    status: 'Connected',
    description: 'Connect and manage data with Google Sheets',
    Icon: BsFileSpreadsheet,
    color: '#0F9D58',
  },
  {
    id: 'telegram',
    label: 'Telegram',
    status: 'Coming Soon',
    description: 'Connect with customers through Telegram messaging',
    Icon: BsSend,
    color: '#0088cc',
  },
  {
    id: 'slack',
    label: 'Slack',
    status: 'Coming Soon',
    description: 'Respond to your website chats right from Slack',
    Icon: BsSlack,
    color: '#825b83',
  },
  {
    id: 'microsoft-teams',
    label: 'Microsoft Teams',
    status: 'Coming Soon',
    description: 'Live Chat with your website visitors right from Microsoft Teams',
    Icon: BsMicrosoftTeams,
    color: '#6264A7',
  },
  {
    id: 'facebook-messenger',
    label: 'Facebook Messenger',
    status: 'Coming Soon',
    description: 'Connect with customers through Facebook Messenger',
    Icon: BsFacebook,
    color: '#0084FF',
  },
  {
    id: 'sms',
    label: 'SMS',
    status: 'Coming Soon',
    description: 'Customers can contact you directly using SMS text messages',
    Icon: BsEnvelope,
    color: '#2D8CFF',
  },
  {
    id: 'zoom',
    label: 'Zoom',
    status: 'Coming Soon',
    description: 'Talk to website visitors from Zoom Chat',
    Icon: BsCameraVideo,
    color: '#2D8CFF',
  },
];

function FeatureSetTiles() {
  const [whatsappModalOpen, setWhatsappModalOpen] = useState(false);
  const [whatsappStatus, setWhatsappStatus] = useState('Idle');

  const fetchStatus = useCallback(async (phoneNumber) => {
    const phone = phoneNumber || sessionStorage.getItem(WHATSAPP_PHONE_SESSION_KEY) || localStorage.getItem(WHATSAPP_PHONE_STORAGE_KEY);
    if (!phone) return;
    try {
      const connected = await checkChannelStatus(phone);
      const status = connected ? 'Connected' : 'Idle';
      setWhatsappStatus(status);
      sessionStorage.setItem(WHATSAPP_STATUS_SESSION_KEY, status);
    } catch {
      // Keep current state on error
    }
  }, []);

  useEffect(() => {
    const sessionStatus = sessionStorage.getItem(WHATSAPP_STATUS_SESSION_KEY);
    if (sessionStatus === 'Connected' || sessionStatus === 'Idle') {
      setWhatsappStatus(sessionStatus);
      return;
    }
    const phone = sessionStorage.getItem(WHATSAPP_PHONE_SESSION_KEY) || localStorage.getItem(WHATSAPP_PHONE_STORAGE_KEY);
    if (phone) {
      fetchStatus();
    }
  }, [fetchStatus]);

  function handleConnectSuccess(whatsappNumber) {
    const number = (whatsappNumber || '').trim().replace(/^\+/, '').replace(/\s/g, '');
    if (number) {
      sessionStorage.setItem(WHATSAPP_PHONE_SESSION_KEY, number);
      localStorage.setItem(WHATSAPP_PHONE_STORAGE_KEY, number);
      fetchStatus(number);
    }
  }

  return (
    <div className="feature-set-tiles">
      <h2 className="feature-set-title">All Features</h2>
      <p className="feature-set-subtitle">Connect with your feature.</p>
      <div className="feature-set-grid">
        {TILES.map(function (tile) {
          const isAvailable = tile.id === 'whatsapp' || tile.id === 'google-sheets';
          const showConnectButton = tile.id === 'whatsapp';
          const IconComponent = tile.Icon;
          const displayStatus = tile.id === 'whatsapp' ? whatsappStatus : tile.status;
          const statusClass = displayStatus.toLowerCase().replace(' ', '-');

          return (
            <div
              key={tile.id}
              className={'feature-tile feature-tile--glass' + (!isAvailable ? ' feature-tile--disabled' : '')}
            >
              <div className="feature-tile-logo">
                {IconComponent ? (
                  <IconComponent 
                    size={48} 
                    className="feature-icon"
                    style={{ color: tile.color }}
                  />
                ) : (
                  <span className="feature-tile-logo-fallback">{tile.fallbackIcon || '📱'}</span>
                )}
              </div>
              <span className="feature-tile-label">{tile.label}</span>
              <p className="feature-tile-description">{tile.description}</p>
              <span className={'feature-tile-status feature-tile-status--' + statusClass}>
                {displayStatus}
              </span>
              {showConnectButton && (
                <button
                  type="button"
                  className="feature-tile-connect"
                  disabled={tile.id === 'whatsapp' && displayStatus === 'Connected'}
                  onClick={function () { 
                    if (tile.id === 'whatsapp') {
                      setWhatsappModalOpen(true);
                    }
                  }}
                >
                  Connect
                </button>
              )}
            </div>
          );
        })}
      </div>
      <WhatsAppConnectModal
        open={whatsappModalOpen}
        onClose={function () { setWhatsappModalOpen(false); }}
        onConnectSuccess={handleConnectSuccess}
      />
    </div>
  );
}

export default FeatureSetTiles;


// import React, { useState } from 'react';
// import {
//   BsWhatsapp,
//   BsGlobe,
//   BsSend,
//   BsMicrosoftTeams,
//   BsFacebook,
//   BsEnvelope,
//   BsCameraVideo,
//   BsSlack,
// } from 'react-icons/bs';
// import WhatsAppConnectModal from './WhatsAppConnectModal';
// import './FeatureSetTiles.css';

// const TILES = [
//   {
//     id: 'whatsapp',
//     label: 'WhatsApp',
//     status: 'Active',
//     description: 'Customers can contact you directly from WhatsApp',
//     Icon: BsWhatsapp,
//     color: '#25D366',
//   },
//   {
//     id: 'google',
//     label: 'Google',
//     status: 'Coming Soon',
//     description: 'Answer website customer chats from Google Chat and Gmail',
//     Icon: BsGlobe,
//     color: '#4285F4',
//   },
//   {
//     id: 'telegram',
//     label: 'Telegram',
//     status: 'Coming Soon',
//     description: 'Connect with customers through Telegram messaging',
//     Icon: BsSend,
//     color: '#0088cc',
//   },
//   {
//     id: 'slack',
//     label: 'Slack',
//     status: 'Coming Soon',
//     description: 'Respond to your website chats right from Slack',
//     Icon: BsSlack,
//     color: '#4A154B',
//   },
//   {
//     id: 'microsoft-teams',
//     label: 'Microsoft Teams',
//     status: 'Coming Soon',
//     description: 'Live Chat with your website visitors right from Microsoft Teams',
//     Icon: BsMicrosoftTeams,
//     color: '#6264A7',
//   },
//   {
//     id: 'facebook-messenger',
//     label: 'Facebook Messenger',
//     status: 'Coming Soon',
//     description: 'Connect with customers through Facebook Messenger',
//     Icon: BsFacebook,
//     color: '#0084FF',
//   },
//   {
//     id: 'sms',
//     label: 'SMS',
//     status: 'Coming Soon',
//     description: 'Customers can contact you directly using SMS text messages',
//     Icon: BsEnvelope,
//     color: '#2D8CFF',
//   },
//   {
//     id: 'zoom',
//     label: 'Zoom',
//     status: 'Coming Soon',
//     description: 'Talk to website visitors from Zoom Chat',
//     Icon: BsCameraVideo,
//     color: '#2D8CFF',
//   },
// ];

// function FeatureSetTiles() {
//   const [whatsappModalOpen, setWhatsappModalOpen] = useState(false);

//   const openWhatsApp = () => {
//     const phone = "94779803384"; // remove + and spaces
//     const message = encodeURIComponent("Hi");
//     const url = `https://wa.me/${phone}?text=${message}`;
//     window.open(url, "_blank");
//   };

//   return (
//     <div className="feature-set-tiles">
//       <h2 className="feature-set-title">All Features</h2>
//       <p className="feature-set-subtitle">Connect with your feature.</p>

//       <div className="feature-set-grid">
//         {TILES.map(function (tile) {
//           const isActive = tile.status === 'Active';
//           const IconComponent = tile.Icon;

//           return (
//             <div
//               key={tile.id}
//               className={
//                 'feature-tile feature-tile--glass' +
//                 (!isActive ? ' feature-tile--disabled' : '')
//               }
//             >
//               <div className="feature-tile-logo">
//                 {IconComponent ? (
//                   <IconComponent
//                     size={48}
//                     className="feature-icon"
//                     style={{ color: tile.color }}
//                   />
//                 ) : (
//                   <span className="feature-tile-logo-fallback">
//                     {tile.fallbackIcon || '📱'}
//                   </span>
//                 )}
//               </div>

//               <span className="feature-tile-label">{tile.label}</span>
//               <p className="feature-tile-description">{tile.description}</p>

//               <span
//                 className={
//                   'feature-tile-status feature-tile-status--' +
//                   tile.status.toLowerCase().replace(' ', '-')
//                 }
//               >
//                 {tile.status}
//               </span>

//               {isActive && (
//                 <button
//                   type="button"
//                   className="feature-tile-connect"
//                   onClick={() => {
//                     if (tile.id === 'whatsapp') {
//                       openWhatsApp();
//                     }
//                   }}
//                 >
//                   Connect
//                 </button>
//               )}
//             </div>
//           );
//         })}
//       </div>

//       <WhatsAppConnectModal
//         open={whatsappModalOpen}
//         onClose={() => setWhatsappModalOpen(false)}
//       />
//     </div>
//   );
// }

// export default FeatureSetTiles;
